import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, Plus, Trash2, FileDown } from 'lucide-react';
import FreeTextNotice from '@/components/FreeTextNotice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyEntry = (siteId, date) => ({
  site_id: siteId,
  date,
  rondes_par_boutique: {},
  // apprehensions_nombre, apprehensions_montant, incendie_rondes,
  // incendie_levee_doute, incendie_essai_alerte, sst_interventions,
  // sst_clients, sst_salaries, sst_malaise et sst_blessure ne sont plus
  // saisis ici : calculés et enregistrés automatiquement depuis la main
  // courante côté serveur (voir compute_auto_report_fields).
  ronde_voiture_qte: 0,
  interventions_galerie: [],
  interventions_exterieur: [],
  interventions_parking: [],
  mendicite_qte: 0,
  circulation_qte: 0,
  circulation_duree_minutes: 0,
  incendie_intervention_ssi: 0,
  incendie_intervention_extinction: 0,
  incendie_controle_extincteurs: 0,
  incendie_essais_sprinkler_par_zone: {},
  incendie_formation_boutiques: 0,
  incendie_formation_ads: 0,
  incendie_demande_intervention_technique: 0,
  sst_pris_en_charge_pc: 0,
  sst_defibrillateur: 0,
  nuit_rondes_qte: 0,
  nuit_rondes_duree_minutes: 0,
  nuit_km_total: 0,
  nuit_incidents_surete: 0,
  nuit_incidents_incendie: 0,
  nuit_incidents_technique: 0,
  nuit_caddies: 0,
  notes: ''
});

const NumField = ({ label, value, onChange, testId }) => (
  <div>
    <Label className="text-[11px] text-slate-600 dark:text-slate-300 leading-tight block mb-0.5">{label}</Label>
    <Input
      type="number"
      min="0"
      value={value}
      onChange={(e) => onChange(e.target.value === '' ? 0 : parseFloat(e.target.value))}
      data-testid={testId}
      className="h-7 text-sm px-2"
    />
  </div>
);

// Liste des interventions d'une zone (galerie/extérieur/parking), chacune avec
// son horaire et son descriptif propre, plutôt qu'un simple total.
const InterventionsList = ({ label, items, onChange }) => {
  const addItem = () => onChange([...items, { time: '', description: '' }]);
  const updateItem = (idx, field, value) => {
    const updated = items.map((it, i) => i === idx ? { ...it, [field]: value } : it);
    onChange(updated);
  };
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{label} ({items.length})</Label>
        <Button type="button" variant="outline" size="sm" onClick={addItem}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-slate-400 dark:text-slate-500">Aucune intervention</p>
      ) : (
        <div className="space-y-1.5">
          {items.map((it, idx) => (
            <div key={idx} className="flex gap-2 items-start p-1.5 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <Input
                type="time"
                value={it.time}
                onChange={(e) => updateItem(idx, 'time', e.target.value)}
                className="w-24 shrink-0 h-7 text-sm px-2"
              />
              <Textarea
                placeholder="Descriptif de l'intervention"
                value={it.description}
                onChange={(e) => updateItem(idx, 'description', e.target.value)}
                rows={1}
                className="flex-1 min-h-7 text-sm py-1"
              />
              <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" onClick={() => removeItem(idx)}>
                <Trash2 className="h-3.5 w-3.5 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Durée saisie en heures + minutes, convertie en minutes totales pour le stockage
const DurationField = ({ label, totalMinutes, onChange }) => {
  const hours = Math.floor((totalMinutes || 0) / 60);
  const minutes = (totalMinutes || 0) % 60;
  return (
    <div>
      <Label className="text-[11px] text-slate-600 dark:text-slate-300 leading-tight block mb-0.5">{label}</Label>
      <div className="flex gap-1 items-center">
        <Input
          type="number" min="0"
          value={hours}
          onChange={(e) => onChange((parseInt(e.target.value, 10) || 0) * 60 + minutes)}
          placeholder="h"
          className="h-7 text-sm px-2 w-16"
        />
        <span className="text-[11px] text-slate-400">h</span>
        <Input
          type="number" min="0" max="59"
          value={minutes}
          onChange={(e) => onChange(hours * 60 + (parseInt(e.target.value, 10) || 0))}
          placeholder="min"
          className="h-7 text-sm px-2 w-16"
        />
        <span className="text-[11px] text-slate-400">min</span>
      </div>
    </div>
  );
};

const getParisToday = () => {
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(new Date()).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  return `${parts.year}-${parts.month}-${parts.day}`;
};

const DailyReportForm = ({ user, onLogout }) => {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedDate, setSelectedDate] = useState(getParisToday());
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reportMonth, setReportMonth] = useState(getParisToday().slice(0, 7));

  const canManage = user?.permission_overrides?.manage_daily_report ?? ['admin'].includes(user?.role);

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/sites`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setSites(data);
        if (data.length > 0) setSelectedSite(data[0].id);
      }
    } catch { /* silencieux */ }
    finally { setLoading(false); }
  };

  const loadEntry = useCallback(async () => {
    if (!selectedSite || !selectedDate) return;
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/daily-reports?site_id=${selectedSite}&start_date=${selectedDate}&end_date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEntry(data.length > 0 ? data[0] : emptyEntry(selectedSite, selectedDate));
      }
    } catch {
      setEntry(emptyEntry(selectedSite, selectedDate));
    } finally {
      setLoading(false);
    }
  }, [selectedSite, selectedDate]);

  useEffect(() => {
    loadEntry();
  }, [loadEntry]);

  const update = (field, value) => setEntry(prev => ({ ...prev, [field]: value }));
  const updateBoutique = (dictField, boutique, value) => {
    setEntry(prev => ({
      ...prev,
      [dictField]: { ...prev[dictField], [boutique]: value }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/daily-reports`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(entry)
      });
      if (res.ok) {
        toast.success('Reporting enregistré');
        loadEntry();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/daily-reports/pdf?site_id=${selectedSite}&date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `reporting_${currentSite?.name || 'site'}_${selectedDate}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la génération du PDF');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  const handleDownloadMonthlyReport = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/daily-reports/monthly-docx?site_id=${selectedSite}&month=${reportMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rapport_mensuel_${currentSite?.name || 'site'}_${reportMonth}.docx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Rapport mensuel téléchargé');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Aucun reporting trouvé pour ce mois');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  const handleDownloadMonthlyReportPdf = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/daily-reports/monthly-pdf?site_id=${selectedSite}&month=${reportMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rapport_mensuel_${currentSite?.name || 'site'}_${reportMonth}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Rapport mensuel PDF téléchargé');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Aucun reporting trouvé pour ce mois');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  const currentSite = sites.find(s => s.id === selectedSite);
  const boutiques = currentSite?.boutiques || [];

  if (!canManage) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="text-center py-20 text-gray-500 dark:text-slate-400">
          Vous n'avez pas accès au reporting quotidien.
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 animate-fade-in" data-testid="daily-report-form">
        <div>
          <h1 className="text-4xl font-bold text-gray-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Reporting quotidien
          </h1>
          <p className="text-gray-600 dark:text-slate-300">Saisie journalière du Chef de Poste (sûreté, incendie, secours à personne, missions de nuit)</p>
        </div>

        <Card className="glass-effect">
          <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Site</Label>
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger data-testid="daily-report-site"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} data-testid="daily-report-date" />
            </div>
          </CardContent>
        </Card>

        {loading || !entry ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : (
          <>
            <Card className="glass-effect">
              <CardHeader><CardTitle className="text-blue-600 dark:text-blue-400">Sûreté</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {boutiques.length > 0 && (
                  <div>
                    <Label className="text-sm font-medium">Rondes par boutique</Label>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-2">
                      {boutiques.map(b => (
                        <NumField
                          key={b}
                          label={b}
                          value={entry.rondes_par_boutique[b] || 0}
                          onChange={(v) => updateBoutique('rondes_par_boutique', b, v)}
                          testId={`ronde-boutique-${b}`}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  Les appréhensions (nombre et montant) sont calculées automatiquement depuis la main courante.
                </p>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  <NumField label="Ronde voiture (qté)" value={entry.ronde_voiture_qte} onChange={(v) => update('ronde_voiture_qte', v)} />
                  <NumField label="Mendicité" value={entry.mendicite_qte} onChange={(v) => update('mendicite_qte', v)} />
                  <NumField label="Gestion circulation (qté)" value={entry.circulation_qte} onChange={(v) => update('circulation_qte', v)} />
                  <NumField label="Gestion circulation (minutes)" value={entry.circulation_duree_minutes} onChange={(v) => update('circulation_duree_minutes', v)} />
                </div>
                <InterventionsList label="Interventions galerie" items={entry.interventions_galerie} onChange={(v) => update('interventions_galerie', v)} />
                <InterventionsList label="Interventions extérieur" items={entry.interventions_exterieur} onChange={(v) => update('interventions_exterieur', v)} />
                <InterventionsList label="Interventions parking" items={entry.interventions_parking} onChange={(v) => update('interventions_parking', v)} />
              </CardContent>
            </Card>

            <Card className="glass-effect">
              <CardHeader><CardTitle className="text-red-600 dark:text-red-400">Incendie</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  Rondes incendie, levée de doute et essai d'alerte sont calculés automatiquement depuis la main courante.
                </p>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  <NumField label="Intervention SSI" value={entry.incendie_intervention_ssi} onChange={(v) => update('incendie_intervention_ssi', v)} />
                  <NumField label="Intervention moyens d'extinction" value={entry.incendie_intervention_extinction} onChange={(v) => update('incendie_intervention_extinction', v)} />
                  <NumField label="Contrôle extincteurs / RIA" value={entry.incendie_controle_extincteurs} onChange={(v) => update('incendie_controle_extincteurs', v)} />
                  <NumField label="Formation boutiques" value={entry.incendie_formation_boutiques} onChange={(v) => update('incendie_formation_boutiques', v)} />
                  <NumField label="Formation ADS" value={entry.incendie_formation_ads} onChange={(v) => update('incendie_formation_ads', v)} />
                  <NumField label="Demande intervention technique" value={entry.incendie_demande_intervention_technique} onChange={(v) => update('incendie_demande_intervention_technique', v)} />
                </div>
                {boutiques.length > 0 && (
                  <div>
                    <Label className="text-sm font-medium">Essais sprinkler par zone</Label>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-2">
                      {boutiques.map(b => (
                        <NumField
                          key={b}
                          label={b}
                          value={entry.incendie_essais_sprinkler_par_zone[b] || 0}
                          onChange={(v) => updateBoutique('incendie_essais_sprinkler_par_zone', b, v)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="glass-effect">
              <CardHeader><CardTitle className="text-green-600 dark:text-green-400">Secours à personne</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  Interventions, clients, salariés, malaises et blessures sont calculés automatiquement depuis la main courante.
                </p>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  <NumField label="Pris en charge au PC" value={entry.sst_pris_en_charge_pc} onChange={(v) => update('sst_pris_en_charge_pc', v)} />
                  <NumField label="Utilisation défibrillateur" value={entry.sst_defibrillateur} onChange={(v) => update('sst_defibrillateur', v)} />
                </div>
              </CardContent>
            </Card>

            <Card className="glass-effect">
              <CardHeader><CardTitle className="text-indigo-600 dark:text-indigo-400">Missions de nuit</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-3 md:grid-cols-6 gap-2">
                <NumField label="Rondes (qté)" value={entry.nuit_rondes_qte} onChange={(v) => update('nuit_rondes_qte', v)} />
                <DurationField label="Durée totale des rondes" totalMinutes={entry.nuit_rondes_duree_minutes} onChange={(v) => update('nuit_rondes_duree_minutes', v)} />
                <NumField label="Kilomètres parcourus" value={entry.nuit_km_total} onChange={(v) => update('nuit_km_total', v)} />
                <NumField label="Incidents - Sûreté" value={entry.nuit_incidents_surete} onChange={(v) => update('nuit_incidents_surete', v)} />
                <NumField label="Incidents - Incendie" value={entry.nuit_incidents_incendie} onChange={(v) => update('nuit_incidents_incendie', v)} />
                <NumField label="Incidents - Technique" value={entry.nuit_incidents_technique} onChange={(v) => update('nuit_incidents_technique', v)} />
                <NumField label="Caddies récupérés" value={entry.nuit_caddies} onChange={(v) => update('nuit_caddies', v)} />
              </CardContent>
            </Card>

            <Card className="glass-effect">
              <CardHeader><CardTitle>Notes / remarques du jour</CardTitle></CardHeader>
              <CardContent>
                <Textarea
                  value={entry.notes || ''}
                  onChange={(e) => update('notes', e.target.value)}
                  rows={3}
                  placeholder="Toute précision utile pour ce jour (facultatif)"
                />
                <FreeTextNotice className="mt-1" />
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button className="flex-1 btn-primary" onClick={handleSave} disabled={saving} data-testid="save-daily-report-btn">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Enregistrement...' : 'Enregistrer le reporting du jour'}
              </Button>
              <Button variant="outline" onClick={handleDownloadPdf} data-testid="download-daily-report-pdf-btn">
                <FileDown className="h-4 w-4 mr-2" />
                PDF
              </Button>
            </div>

            <Card className="glass-effect">
              <CardHeader><CardTitle>Rapport mensuel</CardTitle></CardHeader>
              <CardContent className="flex flex-col sm:flex-row gap-3 items-end">
                <div className="flex-1 w-full">
                  <Label>Mois</Label>
                  <Input
                    type="month"
                    value={reportMonth}
                    onChange={(e) => setReportMonth(e.target.value)}
                    data-testid="monthly-report-month"
                  />
                </div>
                <Button variant="outline" onClick={handleDownloadMonthlyReport} data-testid="download-monthly-report-btn">
                  <FileDown className="h-4 w-4 mr-2" />
                  Télécharger le rapport Word
                </Button>
                <Button variant="outline" onClick={handleDownloadMonthlyReportPdf} data-testid="download-monthly-report-pdf-btn">
                  <FileDown className="h-4 w-4 mr-2" />
                  Télécharger le rapport PDF
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default DailyReportForm;
